import { Injectable } from '@angular/core';
import { LocalPermissionsService } from './local-permissions.service';

/**
 * Serviço de permissões do PWA Angular (projeto standalone, sem AngularJS).
 *
 * Validação pela fonte local (`user-accounts`). O backend continua como
 * proteção final (403) — algumas checagens aqui são permissivas de propósito
 * (UI/conveniência), não são o limite de segurança real.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  constructor(
    private localPermissions: LocalPermissionsService
  ) {}

  isAdmin(): boolean {
    return this.isStandaloneAdmin();
  }

  checkPermissionForPageId(pageId: string): boolean {
    if (this.isAdmin()) {
      return true;
    }
    return this.localPermissions.hasPagePermission(pageId);
  }

  /**
   * Versão estrita para proteção de rota.
   * - allowed: acesso liberado
   * - denied: acesso negado
   * - pending: permissões ainda não carregadas
   */
  getRoutePermissionStatus(pageId: string): 'allowed' | 'denied' | 'pending' {
    if (this.isAdmin()) {
      return 'allowed';
    }

    // Fonte primária: permissões locais carregadas pelo PWA.
    if (this.localPermissions.isLoaded()) {
      return this.localPermissions.hasPagePermission(pageId) ? 'allowed' : 'denied';
    }

    // PWA ainda não terminou bootstrap de permissões.
    return 'pending';
  }

  /**
   * Verifica por state name ou pageId (herdado do `bf-permissions` do AngularJS). Sem
   * `$state`/ui-router aqui — `resolvePageId()` trata a entrada como pageId direto.
   */
  checkPermissionForStateName(stateNameOrPageId: string): boolean {
    return this.checkPermissionForPageId(this.resolvePageId(stateNameOrPageId));
  }

  /**
   * Verifica se uma operação está revogada para o usuário.
   * Retorna `true` se a operação está **revogada** (sem permissão).
   */
  isRevokedOperation(pageId: string, operation: string): boolean {
    if (this.isAdmin()) {
      return false;
    }
    // Se a página não existe no localPermissions, trata como não revogada
    // (permissivo por design — página desconhecida não implica operação revogada).
    if (!this.localPermissions.hasPagePermission(pageId)) {
      return false;
    }
    return this.localPermissions.isOperationRevoked(pageId, operation);
  }

  async ensureLocalPermissionsLoaded(): Promise<void> {
    await this.localPermissions.ensureLoaded();
  }

  /** Sem `$state` neste projeto: sempre trata a entrada como pageId direto. */
  resolvePageId(stateNameOrPageId: string): string {
    return stateNameOrPageId;
  }

  /** Sem `$state` neste projeto standalone: não há state atual. */
  getCurrentPageId(): string | null {
    return null;
  }

  private isStandaloneAdmin(): boolean {
    return !!this.localPermissions.readUserAccessContext()?.admin;
  }
}
